import React, { useState, useEffect, useRef } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, User, LogOut } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import LangSwitcher from "@/components/LangSwitcher";

export default function Navbar() {
  const { t } = useLang();
  const { customer, loginWithGoogle, logout } = useCustomerAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const partnerRef = useRef(null);
  const servicesRef = useRef(null);
  const accountRef = useRef(null);
  const { pathname } = useLocation();

  const NAV = [{ to: "/", label: t("nav.home"), key: "home" }];
  const SERVICES_MENU = [
    { to: "/leistungen", label: t("nav.services"), sub: "Übersicht" },
    { to: "/produktion", label: "Produktion A+++", sub: "BIM · Fertigteile · Montage" },
    { to: "/haeuser", label: "Häuser", sub: "Royalhouse Prefab · Katalog" },
    { to: "/sanierung", label: "Sanierung & Revitalisierung", sub: "Bestand · Tragwerk · Hülle" },
    { to: "/grundstuecke", label: "Grundstücke & Ankauf", sub: "Liegenschaften · Kooperationen" },
    { to: "/investoren", label: "Investoren & Projektpartner", sub: "SPV · Reporting · NDA" },
  ];
  const NAV_AFTER = [
    { to: "/projekte", label: t("nav.projects"), key: "projects" },
    { to: "/ueber-uns", label: t("nav.about"), key: "about" },
    { to: "/kontakt", label: t("nav.contact"), key: "contact" },
  ];
  const PARTNERS = [
    { to: "/partner/budova", label: "Budova", sub: "Industrieller Backbone" },
    { to: "/partner/royalhouse-ua", label: "Royal House Kyiw", sub: "Fertigteilbau Ukraine" },
    { to: "/partner/wellbeing", label: "Well-Being ConTech", sub: "Bautechnologie & Smart Home" },
    { to: "/partner/building-technik", label: "ECO Building Technik", sub: "Haustechnik & Lieferant" },
    { to: "/partner/ukraine-rebuild", label: "Ukraine Rebuilding Alliance", sub: "Wiederaufbau-Initiative" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setPartnerOpen(false);
    setServicesOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onClick = (e) => {
      if (partnerRef.current && !partnerRef.current.contains(e.target)) {
        setPartnerOpen(false);
      }
      if (servicesRef.current && !servicesRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
      if (accountRef.current && !accountRef.current.contains(e.target)) {
        setAccountOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const partnerActive = pathname.startsWith("/partner");
  const servicesActive = ["/leistungen", "/produktion", "/haeuser", "/sanierung", "/grundstuecke", "/investoren"].includes(pathname);
  // Only Home has an immersive hero image behind the navbar — everywhere else the navbar
  // needs a solid backdrop from the start to guarantee legibility.
  const transparentAllowed = pathname === "/";
  const showSolid = scrolled || !transparentAllowed;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        showSolid
          ? "bg-[#051630]/92 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 h-20 flex items-center justify-between gap-6">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center gap-3 group shrink-0"
        >
          <span className="w-9 h-9 border border-white flex items-center justify-center font-display text-lg font-bold tracking-tighter">
            R
          </span>
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-base tracking-widest uppercase">
              Royalhouse
            </span>
            <span className="text-[10px] tracking-[0.3em] uppercase text-white/50">
              Bauträger GmbH
            </span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-7">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-link-${item.key}`}
              className={({ isActive }) =>
                `rh-link text-[12px] tracking-[0.18em] uppercase font-medium ${
                  isActive ? "text-white active" : "text-white/70 hover:text-white"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}

          {/* Services dropdown */}
          <div className="relative" ref={servicesRef}>
            <button
              onClick={() => setServicesOpen((v) => !v)}
              data-testid="nav-link-services"
              className={`rh-link inline-flex items-center gap-1.5 text-[12px] tracking-[0.18em] uppercase font-medium ${
                servicesActive ? "text-white active" : "text-white/70 hover:text-white"
              }`}
            >
              {t("nav.services")}
              <ChevronDown
                size={14}
                strokeWidth={1.5}
                className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`}
              />
            </button>
            {servicesOpen && (
              <div
                className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[360px] bg-[#051630] border border-white/15 backdrop-blur-xl"
                data-testid="nav-services-menu"
              >
                {SERVICES_MENU.map((p) => (
                  <Link
                    key={p.to}
                    to={p.to}
                    data-testid={`nav-services-${p.to.replace("/", "")}`}
                    className="block px-5 py-4 border-b border-white/10 last:border-b-0 hover:bg-white/[0.04] transition-colors"
                    onClick={() => setServicesOpen(false)}
                  >
                    <p className="font-display text-base tracking-tight text-white">{p.label}</p>
                    <p className="text-[10px] tracking-[0.25em] uppercase text-white/50 mt-1">
                      {p.sub}
                    </p>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Partner dropdown */}
          <div className="relative" ref={partnerRef}>
            <button
              onClick={() => setPartnerOpen((v) => !v)}
              data-testid="nav-link-partners"
              className={`rh-link inline-flex items-center gap-1.5 text-[12px] tracking-[0.18em] uppercase font-medium ${
                partnerActive ? "text-white active" : "text-white/70 hover:text-white"
              }`}
            >
              Partner
              <ChevronDown
                size={14}
                strokeWidth={1.5}
                className={`transition-transform ${partnerOpen ? "rotate-180" : ""}`}
              />
            </button>
            {partnerOpen && (
              <div
                className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[320px] bg-[#051630] border border-white/15 backdrop-blur-xl"
                data-testid="nav-partner-menu"
              >
                {PARTNERS.map((p) => (
                  <Link
                    key={p.to}
                    to={p.to}
                    data-testid={`nav-partner-${p.to.split("/").pop()}`}
                    className="block px-5 py-4 border-b border-white/10 last:border-b-0 hover:bg-white/[0.04] transition-colors"
                    onClick={() => setPartnerOpen(false)}
                  >
                    <p className="font-display text-base tracking-tight text-white">{p.label}</p>
                    <p className="text-[10px] tracking-[0.25em] uppercase text-white/50 mt-1">
                      {p.sub}
                    </p>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {NAV_AFTER.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.key}`}
              className={({ isActive }) =>
                `rh-link text-[12px] tracking-[0.18em] uppercase font-medium ${
                  isActive ? "text-white active" : "text-white/70 hover:text-white"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <LangSwitcher />

          {/* Customer account */}
          <div className="relative" ref={accountRef}>
            {customer ? (
              <>
                <button
                  onClick={() => setAccountOpen((v) => !v)}
                  data-testid="navbar-account-btn"
                  className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
                >
                  {customer.picture ? (
                    <img src={customer.picture} alt={customer.name} className="w-8 h-8 rounded-full object-cover border border-white/20" />
                  ) : (
                    <span className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center">
                      <User size={14} strokeWidth={1.5} />
                    </span>
                  )}
                </button>
                {accountOpen && (
                  <div
                    className="absolute top-full right-0 mt-4 w-52 bg-[#051630] border border-white/15 backdrop-blur-xl"
                    data-testid="navbar-account-menu"
                  >
                    <Link
                      to="/konto"
                      onClick={() => setAccountOpen(false)}
                      data-testid="navbar-account-link"
                      className="flex items-center gap-2.5 px-5 py-4 border-b border-white/10 hover:bg-white/[0.04] transition-colors text-sm"
                    >
                      <User size={14} strokeWidth={1.5} /> {t("nav.account")}
                    </Link>
                    <button
                      onClick={() => { setAccountOpen(false); logout(); }}
                      data-testid="navbar-logout-btn"
                      className="w-full flex items-center gap-2.5 px-5 py-4 hover:bg-white/[0.04] transition-colors text-sm text-white/70"
                    >
                      <LogOut size={14} strokeWidth={1.5} /> {t("konto.logout")}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <button
                onClick={() => loginWithGoogle()}
                data-testid="navbar-login-btn"
                className="hidden xl:inline-flex items-center gap-2 text-[11px] font-medium tracking-[0.18em] uppercase text-white/70 hover:text-white transition-colors"
              >
                <User size={14} strokeWidth={1.5} /> {t("nav.login")}
              </button>
            )}
          </div>

          <Link
            to="/kontakt"
            data-testid="navbar-cta"
            className="hidden xl:inline-flex items-center gap-2 px-5 py-2.5 bg-white text-[#051630] text-[11px] font-bold tracking-[0.18em] uppercase hover:bg-transparent hover:text-white border border-white transition-colors"
          >
            {t("nav.cta")}
          </Link>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="xl:hidden text-white p-2"
          aria-label="Menu"
        >
          {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden bg-[#051630] border-t border-white/10" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            {[...NAV, ...NAV_AFTER].map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-mobile-${item.key}`}
                className={({ isActive }) =>
                  `text-sm tracking-[0.18em] uppercase font-medium ${
                    isActive ? "text-white" : "text-white/70"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="pt-2 border-t border-white/10">
              <p className="text-[10px] tracking-[0.3em] uppercase text-white/45 mb-3">{t("nav.services")}</p>
              <div className="flex flex-col gap-3 pl-3">
                {SERVICES_MENU.map((p) => (
                  <NavLink
                    key={p.to}
                    to={p.to}
                    data-testid={`nav-mobile-services-${p.to.replace("/", "")}`}
                    className="text-sm text-white/75 hover:text-white"
                  >
                    {p.label}
                  </NavLink>
                ))}
              </div>
            </div>
            <div className="pt-2 border-t border-white/10">
              <p className="text-[10px] tracking-[0.3em] uppercase text-white/45 mb-3">Partner</p>
              <div className="flex flex-col gap-3 pl-3">
                {PARTNERS.map((p) => (
                  <NavLink
                    key={p.to}
                    to={p.to}
                    data-testid={`nav-mobile-partner-${p.to.split("/").pop()}`}
                    className="text-sm text-white/75 hover:text-white"
                  >
                    {p.label}
                  </NavLink>
                ))}
              </div>
            </div>
            <div className="pt-2">
              <LangSwitcher />
            </div>
            <div className="pt-2 border-t border-white/10">
              {customer ? (
                <div className="flex flex-col gap-3">
                  <NavLink to="/konto" data-testid="navbar-mobile-account-link" className="text-sm text-white/85 flex items-center gap-2">
                    <User size={14} strokeWidth={1.5} /> {t("nav.account")}
                  </NavLink>
                  <button onClick={logout} data-testid="navbar-mobile-logout-btn" className="text-sm text-white/60 flex items-center gap-2">
                    <LogOut size={14} strokeWidth={1.5} /> {t("konto.logout")}
                  </button>
                </div>
              ) : (
                <button onClick={() => loginWithGoogle()} data-testid="navbar-mobile-login-btn" className="text-sm text-white/85 flex items-center gap-2">
                  <User size={14} strokeWidth={1.5} /> {t("nav.login")}
                </button>
              )}
            </div>
            <Link
              to="/kontakt"
              data-testid="navbar-mobile-cta"
              className="mt-2 inline-flex items-center justify-center px-6 py-3 bg-white text-[#051630] text-xs font-bold tracking-[0.18em] uppercase"
            >
              {t("nav.cta")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
